// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type FC, type PropsWithChildren } from 'react';
import {
  AppName,
  BuildEnv,
  NodeEnv,
  appSelect,
  buildEnvSelect,
  generateAppUtils,
  getAppName,
  isAdvisorApp,
  isAnyApp,
  isAnyBuildEnv,
  isAnyNodeEnv,
  isBuildDevelopment,
  isBuildLocal,
  isBuildProduction,
  isClientApp,
  isDevelopment,
  isProduction,
  isTest,
  nodeEnvSelect,
} from '@shared/environment';

export {
  AppName,
  BuildEnv,
  NodeEnv,
  appSelect,
  buildEnvSelect,
  generateAppUtils,
  getAppName,
  isAdvisorApp,
  isAnyApp,
  isAnyBuildEnv,
  isAnyNodeEnv,
  isBuildDevelopment,
  isBuildLocal,
  isBuildProduction,
  isClientApp,
  isDevelopment,
  isProduction,
  isTest,
  nodeEnvSelect,
};

// --- App ---

/**
 * Shows children only when the current app matches one of the provided
 * {@link AppName} values.
 *
 * @example
 *
 * ```tsx
 * <ShowInApp apps={[AppName.Advisor, AppName.Client]}>
 *   <SharedFeature />
 * </ShowInApp>;
 * ```
 */
export const ShowInApp: FC<PropsWithChildren<{ apps: AppName | AppName[] }>> = ({
  children,
  apps,
}) => {
  return isAnyApp(Array.isArray(apps) ? apps : [apps]) ? children : null;
};

/**
 * Shows children only in the Advisor app. Shorthand for `<ShowInApp
 * apps={AppName.Advisor}>`.
 */
export const ShowInAppAdvisor: FC<PropsWithChildren> = ({ children }) => {
  return isAdvisorApp ? children : null;
};

/**
 * Shows children only in the Client app. Shorthand for `<ShowInApp
 * apps={AppName.Client}>`.
 */
export const ShowInAppClient: FC<PropsWithChildren> = ({ children }) => {
  return isClientApp ? children : null;
};

// --- Build env ---

/**
 * Shows children only when the current {@link BuildEnv} matches one of the provided
 * values.
 *
 * @example
 *
 * ```tsx
 * <ShowInBuildEnv envs={[BuildEnv.LOCAL, BuildEnv.DEVELOPMENT]}>
 *   <DevTools />
 * </ShowInBuildEnv>;
 * ```
 */
export const ShowInBuildEnv: FC<
  PropsWithChildren<{ envs: BuildEnv | BuildEnv[] }>
> = ({ children, envs }) => {
  return isAnyBuildEnv(Array.isArray(envs) ? envs : [envs]) ? children : null;
};

/** Shows children only in the `local` build environment. */
export const ShowInBuildLocal: FC<PropsWithChildren> = ({ children }) => {
  return isBuildLocal ? children : null;
};

/** Shows children only in the `development` build environment. */
export const ShowInBuildDevelopment: FC<PropsWithChildren> = ({ children }) => {
  return isBuildDevelopment ? children : null;
};

/** Shows children only in the `production` build environment. */
export const ShowInBuildProduction: FC<PropsWithChildren> = ({ children }) => {
  return isBuildProduction ? children : null;
};

// --- Node env ---

/**
 * Shows children only when the current {@link NodeEnv} matches one of the provided
 * values.
 *
 * @example
 *
 * ```tsx
 * <ShowInNodeEnv envs={[NodeEnv.DEVELOPMENT, NodeEnv.TEST]}>
 *   <DebugPanel />
 * </ShowInNodeEnv>;
 * ```
 */
export const ShowInNodeEnv: FC<
  PropsWithChildren<{ envs: NodeEnv | NodeEnv[] }>
> = ({ children, envs }) => {
  return isAnyNodeEnv(Array.isArray(envs) ? envs : [envs]) ? children : null;
};

/** Shows children only in the `development` Node environment. */
export const ShowInNodeDevelopment: FC<PropsWithChildren> = ({ children }) => {
  return isDevelopment ? children : null;
};

/** Shows children only in the `test` Node environment. */
export const ShowInNodeTest: FC<PropsWithChildren> = ({ children }) => {
  return isTest ? children : null;
};

/** Shows children only in the `production` Node environment. */
export const ShowInNodeProduction: FC<PropsWithChildren> = ({ children }) => {
  return isProduction ? children : null;
};
